<template>
  <div>
    <app-header :title="isEditing ? projectCalculation.name : 'New Project Calculation'">
      <template slot="actions">
        <span v-if="saving" class="text-muted small">
          <span class="spinner-border spinner-border-sm me-1" role="status"></span>
          Saving…
        </span>
        <span v-else-if="isEditing" class="text-muted small">
          <i class="fal fa-check me-1"></i> Saved
        </span>
      </template>
    </app-header>

    <div v-if="loaded">
      <project-calculation-rates-bar
        :project-calculation="projectCalculation"
        @hourly-rate-changed="hourlyRateChanged"
      />

      <div class="container my-5">
        <div class="row">
          <div class="col-12 col-xl-6">
            <project-calculation-project-details-card
              ref="detailsCard"
              :project-calculation="projectCalculation"
              @open-budget-calculator="budgetCalculatorOpen = true"
            />

          </div>

          <div class="col-12 col-xl-6">
            <project-calculation-results
              :project-calculation="projectCalculation"
              :saving="saving"
            />
          </div>
        </div>
      </div>

      <project-calculation-reverse-calc
        :open="budgetCalculatorOpen"
        :project-calculation="projectCalculation"
        @apply="applyReverseEstimate"
        @close="budgetCalculatorOpen = false"
      />
    </div>

    <div v-else class="container my-5 text-center text-muted">
      <span class="spinner-border text-primary"></span>
    </div>
  </div>
</template>

<script>
import { debounce, cloneDeep } from 'lodash'
import { notify } from 'utils'
import { mapActions, mapState } from 'vuex'

import ProjectCalculationModel from '../../models/project-calculation'
import ProjectCalculationRatesBar from './ProjectCalculationRatesBar.vue'
import ProjectCalculationProjectDetailsCard from './ProjectCalculationProjectDetailsCard.vue'
import ProjectCalculationResults from './ProjectCalculationResults.vue'
import ProjectCalculationReverseCalc from './ProjectCalculationReverseCalc.vue'

export default {
  components: {
    ProjectCalculationRatesBar,
    ProjectCalculationProjectDetailsCard,
    ProjectCalculationResults,
    ProjectCalculationReverseCalc,
  },

  props: {
    projectCalculationId: { default: null },
  },

  data() {
    return {
      loaded: false,
      projectCalculation: new ProjectCalculationModel(),
      saving: false,
      pendingSave: false,
      suppressWatch: false,
      budgetCalculatorOpen: false,
    }
  },

  created() {
    this.debouncedAutoSave = debounce(this.autoSave, 800)
  },

  async mounted() {
    if (this.isEditing) {
      await this.getProjectCalculation(this.projectCalculationId)
      this.suppressWatch = true
      this.projectCalculation = cloneDeep(this.savedProjectCalculation)
      await this.$nextTick()
      this.suppressWatch = false
    }

    this.loaded = true
  },

  computed: {
    ...mapState('projectCalculations', { savedProjectCalculation: 'projectCalculation' }),

    isEditing() {
      return !!this.projectCalculationId
    },
  },

  watch: {
    projectCalculation: {
      deep: true,
      handler() {
        if (this.suppressWatch) return
        this.debouncedAutoSave()
      },
    },

    async projectCalculationId(id) {
      if (!id) return

      await this.getProjectCalculation(id)
      this.suppressWatch = true
      this.projectCalculation = cloneDeep(this.savedProjectCalculation)
      await this.$nextTick()
      this.suppressWatch = false
    },
  },

  methods: {
    ...mapActions('projectCalculations', ['storeProjectCalculation', 'updateProjectCalculation', 'getProjectCalculation']),

    hourlyRateChanged(event) {
      this.projectCalculation.hourly_rate_cents = Math.round(parseFloat(event.target.value) * 100)
    },

    applyReverseEstimate(baseEffortEstimation) {
      this.projectCalculation.base_effort_estimation = baseEffortEstimation
    },

    async autoSave() {
      if (this.$refs.detailsCard) {
        const isValid = await this.$refs.detailsCard.$validator.validateAll()
        if (!isValid) return
      }

      if (this.saving) {
        this.pendingSave = true
        return
      }

      this.saving = true
      this.pendingSave = false

      try {
        const payload = {
          name: this.projectCalculation.name,
          client_id: this.projectCalculation.client_id,
          opportunity_id: this.projectCalculation.opportunity_id,
          working_days: this.projectCalculation.working_days,
          hourly_rate_cents: this.projectCalculation.hourly_rate_cents,
          warranty_percentage: this.projectCalculation.warranty_percentage,
          dev_days: this.projectCalculation.dev_days,
          num_devs: this.projectCalculation.num_devs,
          sprint_rituals_estimate: this.projectCalculation.sprint_rituals_estimate,
          scoping_sprint_testing: this.projectCalculation.scoping_sprint_testing,
          project_management: this.projectCalculation.project_management,
          base_effort_estimation: this.projectCalculation.base_effort_estimation,
          evaluation: this.projectCalculation.evaluation,
          contingency_days: this.projectCalculation.contingency_days,
          efficiency: this.projectCalculation.efficiency,
        }

        if (this.isEditing) {
          const saved = await this.updateProjectCalculation({ id: this.projectCalculation.id, payload })
          this.suppressWatch = true
          Object.assign(this.projectCalculation, saved)
          await this.$nextTick()
          this.suppressWatch = false
        } else if (this.projectCalculation.name) {
          const saved = await this.storeProjectCalculation(payload)
          this.$router.replace({
            name: 'project-calculations.edit',
            params: { projectCalculationId: saved.id },
          })
        }
      } catch (error) {
        if (error?.response?.status === 422) {
          notify.error('Validation failed, please check your inputs.')
        }
      } finally {
        this.saving = false
        if (this.pendingSave) {
          this.autoSave()
        }
      }
    },
  },
}
</script>
